<script setup lang="ts">
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Clock3,
  MoreHorizontal,
  Play,
  ShieldAlert,
  UsersRound,
} from 'lucide-vue-next'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useProjectStore } from '@/stores/domain'

const route = useRoute()
const store = useProjectStore()
const project = computed(
  () => store.projects.find((item) => item.id === route.params.projectId),
)
const status = computed(() => (project.value ? store.statuses[project.value.id] : undefined))
</script>

<template>
  <div v-if="project" class="section-page project-detail">
    <RouterLink class="back-link" to="/app/projects"
      ><ArrowLeft :size="15" /> All projects</RouterLink
    >
    <header class="project-detail__header">
      <div>
        <span class="status-pill" :class="`status-pill--${project.health}`"
          ><i></i>{{ project.status.replace('_', ' ') }}</span
        >
        <h1>{{ project.name }}</h1>
        <p>{{ project.objective }}</p>
      </div>
      <div>
        <button class="button button--dark" type="button"><Play :size="15" /> Resume work</button
        ><button class="icon-button" type="button"><MoreHorizontal :size="18" /></button>
      </div>
    </header>
    <div class="metric-strip">
      <div>
        <span>Overall progress</span><strong>{{ project.progressPercent }}%</strong>
        <div class="progress-track">
          <span :style="{ width: `${project.progressPercent}%` }"></span>
        </div>
      </div>
      <div>
        <span>Goals</span><strong>{{ status?.goalSummary.achieved ?? 0 }} <small>of {{ status?.goalSummary.total ?? 0 }} achieved</small></strong>
      </div>
      <div>
        <span>Team</span><strong>3 <small>specialists</small></strong>
      </div>
      <div>
        <span>Next review</span><strong>Friday <small>9:00 AM</small></strong>
      </div>
    </div>
    <div class="detail-grid">
      <section class="detail-card detail-card--wide">
        <div class="detail-card__head">
          <div>
            <span class="eyebrow">Current plan</span>
            <h2>What happens next</h2>
          </div>
          <Clock3 :size="18" />
        </div>
        <ol class="timeline-list">
          <li class="done">
            <CheckCircle2 :size="18" />
            <div>
              <strong>Define ideal customer profile</strong
              ><span>Completed by Sales strategist</span>
            </div>
          </li>
          <li class="active">
            <span class="timeline-pulse"></span>
            <div>
              <strong>Build target account list</strong><span>Ready after CRM connection</span>
            </div>
          </li>
          <li>
            <Circle :size="17" />
            <div><strong>Research decision makers</strong><span>Queued</span></div>
          </li>
          <li>
            <Circle :size="17" />
            <div><strong>Launch approved outreach</strong><span>Requires approval</span></div>
          </li>
        </ol>
      </section>
      <section class="detail-card">
        <div class="detail-card__head">
          <div>
            <span class="eyebrow">Team</span>
            <h2>Specialists</h2>
          </div>
          <UsersRound :size="18" />
        </div>
        <div class="team-list">
          <div>
            <i class="specialist-avatar specialist-avatar--lime">SR</i
            ><span><strong>Sales strategist</strong><small>Waiting</small></span>
          </div>
          <div>
            <i class="specialist-avatar specialist-avatar--purple">LR</i
            ><span><strong>Lead researcher</strong><small>Ready</small></span>
          </div>
          <div>
            <i class="specialist-avatar specialist-avatar--blue">OS</i
            ><span><strong>Outreach specialist</strong><small>Blocked</small></span>
          </div>
        </div>
      </section>
      <section class="detail-card detail-card--warning">
        <div class="detail-card__head">
          <div>
            <span class="eyebrow">Blockers</span>
            <h2>Needs your input</h2>
          </div>
          <ShieldAlert :size="18" />
        </div>
        <p v-if="status?.needsUser.length">{{ status.needsUser.map((item) => item.title).join(', ') }}</p>
        <p v-else>No user action is currently required.</p>
        <RouterLink v-if="status?.needsUser.length" to="/app/approvals">Resolve {{ status.needsUser.length }} items →</RouterLink>
      </section>
    </div>
  </div>
</template>
